import './Nav.css';
import { NavLink } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useContext, useEffect, useState } from 'react';
import { AdminContext } from '../contexts/AdminContext';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import Divider from '@mui/material/Divider';
import { logoUrl } from '../customization/styles';
import { AppEnvSetting, isNullUndefEmpty } from '../ts/common';
import { COSMOS_API, POST_ENDPOINT } from '../api/API';
import Popover from '@mui/material/Popover';
import { RoleObject } from '../interfaces/ApplicationInterfaces';

interface NavProps {
  setSearchReload: React.Dispatch<React.SetStateAction<number>>;
  setRequestsReload: React.Dispatch<React.SetStateAction<number>>;
  setGroupsReload: React.Dispatch<React.SetStateAction<number>>;
  numNotifications: number;
  notificationsHandler: () => void;
  name?: string;
}

const Nav = (props: NavProps) => {
  const { instance } = useMsal();
  const { adminCredentials, getToken } = useContext(AdminContext);
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElLogout, setAnchorElLogout] = useState<null | HTMLElement>(null);
  const [federated, setFederated] = useState(false);
  const [logo, setLogo] = useState(logoUrl);
  const simplifiedUI = AppEnvSetting.simplifiedUI;
  const [showGroupsPage, setShowGroupsPage] = useState<boolean>(false);

  useEffect(() => {
    isFederated();
    if(adminCredentials){
      if(adminCredentials.isSuperAdmin) setShowGroupsPage(true);
      else {
        adminCredentials.roles.some((role: RoleObject)=>
          AppEnvSetting.pageViewAccess?.group?.includes(role.roleId) 
        ) ? setShowGroupsPage(true) : setShowGroupsPage(false);
      }
    }else{
      setShowGroupsPage(false);
    }
  }, [adminCredentials])

  const isFederated = async () => {
    
    let orgData = await COSMOS_API.POST(
      { id: adminCredentials.organizationId },
      POST_ENDPOINT.GetOrganizationData,
      getToken()
    );

    if (
      orgData.ok &&
      orgData.respBody !== null &&
      orgData.respBody !== undefined
    ) {
      if(!isNullUndefEmpty(orgData.respBody.iconURL)) {
        try{
        let url = atob(orgData.respBody.iconURL);
        setLogo(url);
        }
        catch { console.log("Could not load logo")}
      }
    }

    if (!adminCredentials.isSuperAdmin) {

      if (
        orgData.ok &&
        orgData.respBody !== null &&
        orgData.respBody !== undefined
      ) {
        if (
          orgData.respBody.idpConfig !== null &&
          orgData.respBody.idpConfig !== undefined &&
          orgData.respBody.idpConfig.protocol !== 'local'
        ) {
          setFederated(true);
          return;
        }
      }
    }

    setFederated(false);
  }

  const handleClickLogout = (event: React.MouseEvent<HTMLLIElement>) => {
    setAnchorElLogout(event.currentTarget);
  };

  const handleCloseLogout = () => {
    setAnchorElLogout(null);
  };

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const logout = async () => {
    await COSMOS_API.POST({}, POST_ENDPOINT.Logout, getToken());
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  return (
    <>
      <AppBar component="header" position="fixed">
        <Toolbar
          sx={{
            color: 'white',
            backgroundColor: AppEnvSetting.pallette.primary.main,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', marginRight: '41px' }} alignItems={'center'}>
            <img src={logo} alt='Organization logo' className="logo" />
            <Typography variant="h6" sx={{ ml: '15px', mr: '15px' }}>
              {!isNullUndefEmpty(logo) ? '| ' : ''}
              {!adminCredentials.isSuperAdmin &&
              adminCredentials.organizationList.length > 0
                ? `${adminCredentials.organizationList.filter(orgData => orgData.id === adminCredentials.organizationId)[0].name} | `
                : ' '}
              User Management
            </Typography>
          </Box>
          <>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                sx={{ color: AppEnvSetting.pallette.secondary.main}}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {!simplifiedUI || adminCredentials.isSuperAdmin ? (
                    <MenuItem onClick={handleCloseNavMenu} key='review-requests'>
                      <Link
                        component={NavLink}
                        to="/review-requests"
                        onClick={() => {
                          props.setRequestsReload(Date.now);
                        }}
                      >
                        Review Requests
                      </Link>
                    </MenuItem>
                ) : (
                  null
                )}
                <MenuItem onClick={handleCloseNavMenu} key='search-users'>
                  <Link
                    component={NavLink}
                    to="/search-users"
                    onClick={() => {
                      props.setSearchReload(Date.now);
                    }}
                  >
                    Search Users
                  </Link>
                </MenuItem>
                {showGroupsPage && AppEnvSetting.showGroupsPage && 
                <MenuItem onClick={handleCloseNavMenu} key='groups'>
                  <Link
                    component={NavLink}
                    to="/groups"
                    onClick={() => {
                      props.setGroupsReload(Date.now);
                    }}
                  >
                    Groups
                  </Link>
                </MenuItem>}
                {(!AppEnvSetting.disableNewUserCreation) && adminCredentials.generalPermissions.createUsers && (AppEnvSetting.allowStubUsersForAccountLinking || !federated) ? (
                  <MenuItem onClick={handleCloseNavMenu} key='new-user-mgmt'>
                    <Link component={NavLink} to="/new-user-mgmt">
                      New User Management
                    </Link>
                  </MenuItem>
                ) : null}
                {!simplifiedUI || adminCredentials.isSuperAdmin ? (
                  [
                    <MenuItem onClick={handleCloseNavMenu} key='notifications'>
                      <Link component={NavLink} to="/notifications">
                        Notifications
                      </Link>
                    </MenuItem>,
                    // <MenuItem onClick={handleCloseNavMenu} key='support-requests'>
                    //   <Link component={NavLink} to="/support-requests">
                    //     Support Requests
                    //   </Link>
                    // </MenuItem>
                  ]
                ) : (
                  null
                )}
                {adminCredentials.generalPermissions.manageAdmins ? (
                  <MenuItem onClick={handleCloseNavMenu} key='admin'>
                    <Link component={NavLink} to="/admin">
                      Admin
                    </Link>
                  </MenuItem>
                ) : null}
                <Divider />
                <MenuItem onClick={handleCloseNavMenu}>
                  <Button variant="text" onClick={logout}>
                    Log Out
                  </Button>
                </MenuItem>
              </Menu>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'row',
                flexGrow: 1
              }}
            >
              <nav className="desktop" style={{
                width:'100%'
              }}>
                <List>
                  {!simplifiedUI || adminCredentials.isSuperAdmin ? (
                    <ListItem key='review-requests-desktop'>
                      <Link                       
                        component={NavLink}
                        to="/review-requests"
                        className="linkStyle"
                        onClick={() => {
                          props.setRequestsReload(Date.now);
                        }}
                      >
                        Review Requests
                      </Link>
                    </ListItem>
                  ) : (
                    <></>
                  )}
                  <ListItem key='search-users-desktop'>
                    <Link                       
                      component={NavLink}
                      to="/search-users"
                      className="linkStyle"
                      onClick={() => {
                        props.setSearchReload(Date.now);
                      }}
                    >
                      Search Users
                    </Link>
                  </ListItem>
                  {showGroupsPage && AppEnvSetting.showGroupsPage && 
                  <ListItem key='groups-desktop'>
                    <Link                       
                      component={NavLink}
                      to="/groups"
                      className="linkStyle"
                      onClick={() => {
                        props.setGroupsReload(Date.now);
                      }}
                    >
                      Groups
                    </Link>
                  </ListItem>}
                  {(!AppEnvSetting.disableNewUserCreation) && adminCredentials.generalPermissions.createUsers && (AppEnvSetting.allowStubUsersForAccountLinking || !federated) ? (
                    <ListItem key='new-user-mgmt-desktop'>
                      <Link                       
                        component={NavLink}
                        to="/new-user-mgmt"
                        className="linkStyle"
                      >
                        New User Management
                      </Link>
                    </ListItem>
                  ) : (
                    <></>
                  )}
                  {!simplifiedUI || adminCredentials.isSuperAdmin ? (
                    <>
                      <ListItem key='notifications-desktop'>
                        <Link                         
                          component={NavLink}
                          to="/notifications"
                          className="linkStyle"
                        >
                          Notifications
                        </Link>
                      </ListItem>
                      {/* <ListItem key='support-requests-desktop'>
                        <Link                       
                          component={NavLink}
                          to="/support-requests"
                          className="linkStyle"
                        >
                          Support Requests
                        </Link>
                      </ListItem> */}
                    </>
                  ) : (
                    <></>
                  )}
                  {adminCredentials.generalPermissions.manageAdmins ? (
                    <ListItem key='admin-desktop'>
                      <Link
                        component={NavLink}
                        to="/admin"
                        className="linkStyle"
                      >
                        Admin
                      </Link>
                    </ListItem>
                  ) : (
                    <></>
                  )}
                </List>
              </nav>
            </Box>
          </>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'row',
              cursor: 'pointer'
            }}
            alignItems={'center'}
          >
            <List sx={{display: 'flex', flexDirection: 'row'}}>
            {!simplifiedUI || adminCredentials.isSuperAdmin ? (
              <ListItem sx={{marginRight: '20px'}}>
                <IconButton
                  className="bell-icon linkStyle"
                  onClick={props.notificationsHandler}
                >
                  <Typography variant="subtitle1">
                    {props.numNotifications}{' '}
                  </Typography>
                  <NotificationImportantIcon
                    fontSize="small"
                    className="linkStyle"
                  />
                </IconButton>
              </ListItem>
            ) : (
              <></>
            )}
          
            <ListItem aria-describedby="logout-popup" className="linkStyle" onClick={handleClickLogout}>
            <PersonIcon fontSize="small" /> <Typography variant="subtitle1" sx={{width: 'max-content'}}>{props.name ? props.name : ''}</Typography>
            </ListItem>
            </List>
            <Popover
              id="logout-popup"
              open={Boolean(anchorElLogout)}
              anchorEl={anchorElLogout}
              onClose={handleCloseLogout}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <Box sx={{padding: '20px', cursor: 'pointer'}} onClick={logout}>
              Log Out
              </Box>
            </Popover>
            
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar />
      {/* <AdminOrgChange/> */}
    </>
  );
};

export default Nav;
